import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { Link } from "react-router-dom";
import { CheckCircleOutline } from "@mui/icons-material";

export default function AboutUs() {
  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: "url('/images/AboutUs01.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: { xs: "25vh", sm: "40vh", md: "60vh", xl: "80vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "white",
          margin: 2,
          borderRadius: 5,
          p: 3,
          boxShadow: "0 10px 20px rgba(0,0,0,0.3)",
        }}
      ></Box>

      {/* Introduction */}
      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography
          variant="h3"
          fontWeight="bold"
          color="primary"
          sx={{ fontFamily: "Playfair Display, serif" }}
        >
          About Sonika The Chef
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 400,
            fontFamily: "Lora, serif",
            mt: 2,
          }}
        >
          Welcome to <strong>Sonika The Chef</strong> – A delightful space where
          passion meets creativity in the kitchen! 🍽️
        </Typography>
      </Box>

      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          color="primary"
          sx={{ mt: 4 }}
        >
          Meet Sonika
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            px: { xs: 3, md: 5 },
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          Sonika is a passionate <strong>food vlogger and home chef</strong> who
          launched her YouTube channel, <strong>Sonika The Chef</strong>, in{" "}
          <strong>June 2024</strong>. As a homemaker and a loving mother, she
          beautifully balances{" "}
          <strong>caring for her children and managing a home</strong> while
          pursuing her deep love for cooking. Her dream is to create a{" "}
          <strong>strong presence in the culinary world</strong> and inspire
          food lovers globally.
        </Typography>
      </Box>

      {/* Journey */}
      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          Our Journey
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            px: { xs: 3, md: 5 },
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          Cooking has always been Sonika’s biggest love, and she believes that{" "}
          <strong>great food brings happiness to every home</strong>. Through
          her channel, she shares{" "}
          <strong>
            authentic recipes, quick meal ideas, and innovative twists on
            traditional dishes
          </strong>
          , making cooking simple, fun, and accessible for everyone.
        </Typography>
      </Box>

      {/* Mission & Vision */}
      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          Our Mission & Vision
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            px: { xs: 3, md: 5 },
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          Sonika’s dream is to{" "}
          <strong>create a vibrant community of food lovers</strong>, where
          anyone—whether a beginner or an expert—can find inspiration. She
          aspires to{" "}
          <strong>
            grow her channel into a well-known name in the food blogging world
          </strong>
          , bringing unique and flavorful recipes to food enthusiasts worldwide.
        </Typography>
      </Box>

      {/* What We Offer Section */}
      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          What We Offer
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            px: { xs: 3, md: 5 },
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          At <strong>Sonika The Chef</strong>, we bring you a delightful mix of{" "}
          <strong>authentic, innovative, and easy-to-make recipes</strong> that
          cater to all taste buds. Whether you're a beginner in the kitchen or
          an experienced cook looking for fresh inspiration, we have something
          special for you!
        </Typography>

        <Grid container spacing={3} sx={{ mt: 4 }}>
          {[
            {
              title: "Delicious & Authentic Recipes",
              desc: "Discover a variety of homemade recipes, ranging from traditional Indian sweets to modern fusion dishes. Each recipe is carefully curated to bring out the best flavors while keeping the cooking process simple and enjoyable.",
            },
            {
              title: "Step-by-Step Video Tutorials",
              desc: "Our detailed and easy-to-follow video recipes on Sonika The Chef YouTube Channel make cooking a hassle-free experience. Watch, learn, and create mouthwatering dishes with clear instructions and pro tips.",
            },
            {
              title: "Quick & Easy Meals",
              desc: "We understand the importance of time-saving recipes, especially for busy moms and working individuals. Find quick snacks, healthy meals, and one-pot wonders that can be made in no time without compromising on taste.",
            },
            {
              title: "Festival & Special Occasion Dishes",
              desc: "Celebrate every occasion with festive recipes! From Diwali sweets to Holi snacks and Navratri dishes, enjoy traditional flavors for every festival.",
            },
            {
              title: "Tips & Tricks for Better Cooking",
              desc: "Master the art of cooking with our pro tips, kitchen hacks, and expert advice to enhance flavors, save time, and make your cooking more efficient and fun.",
            },
            {
              title: "Food Plating & Presentation Ideas",
              desc: "Great food deserves a great presentation! Learn simple yet elegant plating techniques to make your dishes look as amazing as they taste.",
            },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  p: 2,
                  boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <CheckCircleOutline color="primary" fontSize="large" />
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontSize: "1rem" }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Call to Action */}
      <Box sx={{ textAlign: "center", my: 5 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Join Us on This Delicious Journey! 🎥✨
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            px: { xs: 3, md: 5 },
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          Follow <strong>Sonika The Chef</strong> on YouTube and be part of a
          <strong> growing food community</strong> that loves to cook, share,
          and enjoy delicious homemade food!
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={Link}
          to="/recipes"
          sx={{
            mt: 3,
            background: "linear-gradient(90deg, #FF9800 0%, #FF5722 100%)",
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: "white",
            px: 4,
            py: 1.5,
            borderRadius: 3,
            boxShadow: "0 5px 15px rgba(255, 87, 34, 0.3)",
            "&:hover": {
              background: "linear-gradient(90deg, #FF5722 0%, #E64A19 100%)",
            },
          }}
        >
          🍽️ Explore Recipes
        </Button>
      </Box>
    </Container>
  );
}
